import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface LoginRequest { email: string; password: string; }
export interface CurrentUser {
  id: string;
  fullName: string;
  email: string;
  role: string;       // primary role (kept for backward compat)
  roles: string[];    // all assigned roles
  companyId: string;
  tenantId: string;
}
export interface AuthResponse {
  token?: string;
  accessToken?: string;
  refreshToken?: string;
  user?: CurrentUser;
  requiresMfa?: boolean;
  userId?: string;
  role?: string;
  fullName?: string;
  tenantId?: string;
  companyId?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private currentUserSubject = new BehaviorSubject<CurrentUser | null>(null);
  currentUser$ = this.currentUserSubject.asObservable();

  constructor(private http: HttpClient) {
    const stored = localStorage.getItem('sw_user');
    if (stored) {
      try { this.currentUserSubject.next(JSON.parse(stored)); }
      catch { localStorage.removeItem('sw_user'); }
    }
  }

  login(req: LoginRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${environment.apiUrl}/auth/login`, req).pipe(
      tap(res => {
        if (res.requiresMfa) return; // MFA challenge — tokens not issued yet
        this._storeSession(res);
      })
    );
  }

  forgotPassword(email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${environment.apiUrl}/auth/forgot-password`, { email });
  }

  resetPassword(token: string, newPassword: string): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/auth/reset-password`, { token, newPassword });
  }

  completeMfaLogin(userId: string, code: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${environment.apiUrl}/auth/mfa/verify-login`, { userId, code }).pipe(
      tap(res => this._storeSession(res))
    );
  }

  private _storeSession(res: AuthResponse): void {
    const token: string = res.token ?? res.accessToken ?? (res as any).access_token ?? '';
    const refresh: string = res.refreshToken ?? (res as any).refresh_token ?? '';
    if (token) localStorage.setItem('sw_token', token.trim());
    if (refresh) localStorage.setItem('sw_refresh', refresh.trim());
    const user: CurrentUser = res.user ?? {
      id: (res as any).id ?? (res as any).userId ?? '',
      fullName: (res as any).fullName ?? (res as any).name ?? (res as any).userName ?? '',
      email: (res as any).email ?? '',
      role: (res as any).role ?? (res as any).userRole ?? '',
      roles: (res as any).roles ?? [],
      companyId: (res as any).companyId ?? '',
      tenantId: (res as any).tenantId ?? '',
    };
    if (!Array.isArray(user.roles) || user.roles.length === 0) {
      user.roles = user.role ? [user.role] : [];
    }
    if (user.role && !user.roles.map(r => r.toLowerCase()).includes(user.role.toLowerCase())) {
      user.roles = [user.role, ...user.roles];
    }
    localStorage.setItem('sw_user', JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  logout(): void {
    // Notify backend to invalidate refresh token
    const token = localStorage.getItem('sw_token');
    if (token) {
      this.http.post(`${environment.apiUrl}/auth/logout`, {}).subscribe({ error: () => {} });
    }
    localStorage.removeItem('sw_token');
    localStorage.removeItem('sw_refresh');
    localStorage.removeItem('sw_user');
    this.currentUserSubject.next(null);
  }

  setPushToken(pushToken: string): void {
    this.http.patch(`${environment.apiUrl}/auth/push-token`, { pushToken }).subscribe({ error: () => {} });
  }

  get currentUser() { return this.currentUserSubject.value; }
  get isLoggedIn() { return !!localStorage.getItem('sw_token'); }
  get isSuperAdmin() { return (this.currentUser?.role ?? '').toLowerCase() === 'superadmin'; }
  get tenantId() { return this.currentUser?.tenantId ?? ''; }

  /** True if the current user has ALL of the given roles */
  hasRole(...roles: string[]): boolean {
    const userRoles = (this.currentUser?.roles ?? [this.currentUser?.role ?? '']).map(r => r.toLowerCase());
    return roles.every(r => userRoles.includes(r.toLowerCase()));
  }

  /** True if the current user has ANY of the given roles */
  hasAnyRole(...roles: string[]): boolean {
    const userRoles = (this.currentUser?.roles ?? [this.currentUser?.role ?? '']).map(r => r.toLowerCase());
    return roles.some(r => userRoles.includes(r.toLowerCase()));
  }
}
